import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { SpanishHero } from "@/components/SpanishHero";
import { SpanishWhyUs } from "@/components/SpanishWhyUs";
import { SpanishServices } from "@/components/SpanishServices";
import { SpanishTestimonials } from "@/components/SpanishTestimonials";
import { SpanishFAQ } from "@/components/SpanishFAQ";
import { SpanishFinalCTA } from "@/components/SpanishFinalCTA";
import { LocationContext } from "@/lib/locations";

import { Guarantee } from "@/components/Guarantee";
import { PricingCalculator } from "@/components/PricingCalculator";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { InternalLinks } from "@/components/InternalLinks";
import { MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

const NearbyAreas = () => {
  const { ref, isVisible } = useScrollAnimation();
  const areas = [
    { zone: "Irvine", cities: "Irvine, Tustin, Lake Forest, Foothill Ranch" },
    { zone: "Huntington Beach", cities: "Huntington Beach, Westminster, Fountain Valley, Seal Beach" },
    { zone: "Newport Beach", cities: "Newport Beach, Costa Mesa, Laguna Beach, Laguna Niguel" },
    { zone: "Anaheim", cities: "Anaheim, Fullerton, Brea, Placentia, Yorba Linda" },
  ];
  return (
    <section className="py-16 bg-muted/30">
      <div className="container">
        <div ref={ref} className={cn("text-center mb-10 anim-fade-up", isVisible && "is-visible")}>
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Ciudades que Servimos en Orange County</h2>
          <p className="text-muted-foreground">Limpieza profesional a domicilio en Irvine, Huntington Beach, Newport Beach, Anaheim y todo el Condado de Orange.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {areas.map((area, i) => (
            <div
              key={i}
              className={cn("bg-background rounded-2xl p-6 border shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 text-center anim-fade-up", isVisible && "is-visible")}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <MapPin className="h-6 w-6 text-primary mx-auto mb-3" />
              <h3 className="font-bold text-primary mb-2">{area.zone}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{area.cities}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center flex flex-col sm:flex-row justify-center gap-4">
          <Button asChild size="lg" className="btn-pulse bg-primary text-primary-foreground hover:bg-primary-hover font-bold px-10 py-6 h-auto rounded-2xl shadow-lg">
            <a href={BOOKING_URL}>Reservar Ahora en Orange County</a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/5 hover:text-primary font-bold px-10 py-6 h-auto rounded-2xl">
            <a href={BOOKING_URL}>Ver Mi Precio al Instante</a>
          </Button>
        </div>
      </div>
    </section>
  );
};

const OrangeCountySpanishPage = () => {
  return (
    <LocationContext.Provider value={{ slug: "orange-county", name: "Orange County", type: "region", regionSlug: "orange-county" }}>
      <SEO
        title="Limpieza del Hogar en Orange County, CA — Uplevel Cleaning"
        description="Servicio profesional de limpieza del hogar en Irvine, Huntington Beach, Newport Beach, Anaheim y todo Orange County. Precios planos, reserva en 60 segundos, garantía del 200%."
        keywords="limpieza del hogar orange county, servicio de limpieza irvine, limpieza profunda huntington beach, empleada doméstica newport beach, maid service orange county español, limpieza anaheim fullerton, servicio limpieza condado orange"
        url="https://uplevelcleaning.com/es/orange-county"
        locationName="Orange County"
        regionSlug="orange-county"
        lang="es"
        includeLocalBusiness={true}
        includeFAQ={true}
        breadcrumbs={[
          { name: "Inicio", url: "/" },
          { name: "En Español", url: "/es/sacramento" },
          { name: "Orange County", url: "/es/orange-county" }
        ]}
        reviews={[
          { author: "Gabriela M.", rating: 5, reviewBody: "Vivo en Irvine y nunca había encontrado un servicio de limpieza tan confiable. ¡Uplevel es increíble! Puntuales, amables y dejan todo perfecto.", datePublished: "2026-03-18" },
          { author: "Fernando R.", rating: 5, reviewBody: "Usamos el servicio mensual para nuestra casa en Huntington Beach. El equipo siempre llega a tiempo y el trabajo es impecable. Precio exacto sin sorpresas.", datePublished: "2026-02-12" },
          { author: "Marisol T.", rating: 5, reviewBody: "La limpieza profunda en Newport Beach superó todas mis expectativas. El equipo fue muy profesional y atento a cada detalle. ¡Los recomiendo ampliamente!", datePublished: "2026-01-20" }
        ]}
      />
      <div className="flex min-h-screen flex-col" lang="es">
        <StickyBanner />
        <Navbar />
        <main className="flex-1">
          <SpanishHero cityName="Orange County" regionSlug="orange-county" />
          <div className="bg-primary/5 border-b border-primary/10 py-4">
            <div className="container flex flex-wrap justify-center gap-6 text-sm font-medium text-foreground">
              {[
                "Servicio Local en Orange County",
                "Irvine, Huntington Beach, Newport y Más",
                "Garantía del 200%",
                "Precios Transparentes",
                "Reserva en 60 Segundos",
              ].map((item, i) => (
                <span key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> {item}
                </span>
              ))}
            </div>
          </div>
          <SpanishWhyUs cityName="Orange County" />
          <NearbyAreas />
          <SpanishServices />

          <FrequencyDiscounts />
          <PricingCalculator />
          <SpanishTestimonials region="orange-county" />
          <Guarantee />
          <SpanishFAQ cityName="Orange County" />
          <InternalLinks />
          <SpanishFinalCTA cityName="Orange County" />
        </main>
        <Footer />
      </div>
    </LocationContext.Provider>
  );
};

export default OrangeCountySpanishPage;
